import { useRef, useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useAuth0 } from "@auth0/auth0-react";
import { useTranslation } from "react-i18next";
import { Formik, Form } from "formik";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import SoftButton from "components/SoftButton";
import validations from "./schemas/validations";
import { flow as flowField } from "./schemas/flow";
import initialValues from "./schemas/initialValues";
import { toast } from "react-toastify";
import { closeNewFlowDialog, createNewFlow } from "redux/actions/portal/flowsActionCreator";
import { truncateInputValue } from "helpers/fieldListeners";
import SoftTypography from "components/SoftTypography";
import SoftBox from "components/SoftBox";
import { NewFlowTrigger } from "./trigger";
import "./trigger";
import { useIntegrations } from "hooks/useIntegrations";
import CreatableSoftSelect from "../../components/CreatableSoftSelect";
import { tryCatchWrapper, convertToUndescoreSeparatedString, uploadFile } from "helpers/common";
import { insertIntegrations } from "api/Integrations";
import { addIntegration } from "redux/actions/portal/integrationActionCreator";
import { TRIGGER_TYPES, TRIGGER_ICON_BY_TYPE, TRIGGER_NAMES } from "constants/flowDesigner";
import { usePortalFlows } from "hooks/usePortalFlows";
import defaultFlowIcon from "assets/images/for-flow/flow-icon-list-view.svg";
import CxfFormField from "cxfcomponents/FormField";
import { initialNodes, initialEdges } from "layouts/Flow Designer/components/Flow";
import { EditPictureMenu } from "components/Picture/Edit";

export const newFlowTriggersData = [
   {
      icon: TRIGGER_ICON_BY_TYPE[TRIGGER_TYPES.WEBHOOK],
      label: TRIGGER_NAMES.WEBHOOK,
      description: "Start your flow by a request to a custom URL",
      type: TRIGGER_TYPES.WEBHOOK
   },
   {
      icon: TRIGGER_ICON_BY_TYPE[TRIGGER_TYPES.REST_ENDPOINT],
      label: TRIGGER_NAMES.REST_ENDPOINT,
      description: "Start your flow via REST",
      type: TRIGGER_TYPES.REST_ENDPOINT
   },
   {
      icon: TRIGGER_ICON_BY_TYPE[TRIGGER_TYPES.SCHEDULE],
      label: TRIGGER_NAMES.SCHEDULE,
      description: "Start your flow on a custom schedule",
      type: TRIGGER_TYPES.SCHEDULE
   },
   {
      icon: TRIGGER_ICON_BY_TYPE[TRIGGER_TYPES.REDIS],
      label: TRIGGER_NAMES.REDIS,
      description: "Start your flow by a message from a Redis channel",
      type: TRIGGER_TYPES.REDIS
   },
   {
      icon: TRIGGER_ICON_BY_TYPE[TRIGGER_TYPES.MCP],
      label: TRIGGER_NAMES.MCP,
      description: "Start your flow using Model Context Protocol (MCP)",
      type: TRIGGER_TYPES.MCP
   }
];

function NewFlowDialog() {
   const { user } = useAuth0();
   const { showNewFlowDialog } = useSelector((store) => store?.portal?.flows);
   const dispatch = useDispatch();
   const { t } = useTranslation("flowDesigner");
   const [submitting] = useState(false);
   const [wizardStep, setWizardStep] = useState(1);
   const [triggersAdded, setTriggersAdded] = useState([]);
   const { integrations } = useIntegrations(showNewFlowDialog);
   const [selectedIntegrations, setSelectedIntegrations] = useState([]);
   const { flows } = usePortalFlows(showNewFlowDialog);
   const [pictureUpload, setPictureUpload] = useState(null);

   const {
      formId,
      formField: { flowName, description, integration }
   } = flowField;

   const formRef = useRef(null);

   const onClose = () => {
      dispatch(closeNewFlowDialog());
   };

   const cancelBtnHandler = () => {
      onClose();
   };

   const createFlowIntegrationValues = async (formValues) => {
      const flowIntegrations = [];

      const newIntegrations = Array.isArray(formValues?.integration) ? formValues.integration.filter((integration) => !integrations.find((portalIntegration) => portalIntegration.id === integration)) : [];

      const existingIntegrations = Array.isArray(formValues?.integration) ? formValues.integration.filter((integration) => integrations.find((portalIntegration) => portalIntegration.id === integration)) : [];

      existingIntegrations.forEach((integration) => flowIntegrations.push(integration));

      const handleCreateNewIntegrations = async () => {
         const results = await Promise.all(
            newIntegrations.map(async (integration) => {
               const input = {
                  tenant_id: user.stripeId,
                  integration_name: integration,
                  flow_ids: [],
                  description: "",
                  create_user_id: user.cxfUserId,
                  modified_user_id: user.cxfUserId
               };
               return Object.assign(await insertIntegrations(input), input);
            })
         );

         results.forEach((result) => {
            flowIntegrations.push(result[0].id);
            dispatch(addIntegration(result[0]));
         });
      };

      await tryCatchWrapper(handleCreateNewIntegrations)();

      return flowIntegrations;
   };

   const handleSaveSubmit = async (values, actions) => {
      if (wizardStep !== 2) {
         setWizardStep(2);

         return;
      }

      const tenant_id = user?.stripeId;
      const stepsData = {
         nodes: [],
         edges: [],
         viewport: {
            x: 0,
            y: 0,
            zoom: 1
         },
         triggers: []
      };
      let contextTriggers = {};

      if (triggersAdded.length) {
         stepsData.triggers = triggersAdded
            .filter((trigger) => trigger.type !== "default" && trigger.type !== TRIGGER_TYPES.SCHEDULE)
            .map((trigger) => {
               contextTriggers = {
                  ...contextTriggers,
                  [convertToUndescoreSeparatedString(trigger.label)]: {}
               };
               return {
                  label: trigger.label,
                  type: trigger.type,
                  properties: {}
               };
            });
      }

      stepsData.context = {
         settings: {},
         flow_name: values.flowName,
         steps: {},
         triggers: contextTriggers,
         variables: {},
         session: null,
         tenant_id: user.stripeId
      };

      const integrations = await createFlowIntegrationValues(values);

      stepsData.context.integration_ids = integrations ?? [];

      stepsData.nodes = initialNodes;
      stepsData.edges = initialEdges;

      const steps = JSON.stringify(stepsData);

      const input = {
         created_by: user?.name,
         created_by_id: user?.cxfUserId,
         description: values.description || "",
         group_id: "",
         group_name: "",
         name: values.flowName,
         platform_name: "",
         platform_type: "",
         shared_link: "",
         shared_status: "test",
         steps: `${steps}`,
         trashed: false,
         notes: "",
         flow_settings: null,
         integrations: JSON.stringify(integrations),
         current_version: "1.0"
      };

      try {
         if (pictureUpload) {
            const result = await uploadFile(user, pictureUpload, "flows", input.name, true);
            input.logo = result?.Location;
         }

         dispatch(createNewFlow(tenant_id, input, user, triggersAdded));
         actions.resetForm();
         onClose();
      } catch (error) {
         console.error(error);
         toast.error("Failed to save the flow");
      }
   };

   const handleSubmit = (event) => {
      event.preventDefault();
      formRef.current.submitForm();
   };

   const validateFlowName = (value) => {
      let error;

      if (!value) {
         error = `${flowName.label} is required.`;
         return error;
      }

      if (flows && flows.length > 0) {
         const flowExists = flows.some((flow) => flow.name.toLowerCase() === value.toLowerCase());
         if (flowExists) {
            error = `${value} already exists.`;
            return error;
         }
      }
   };

   const handleTriggerAddition = (newTrigger) => {
      if (triggersAdded.some((trigger) => trigger.type === newTrigger.type)) {
         return setTriggersAdded((prev) => prev.filter((trigger) => trigger.type !== newTrigger.type));
      }

      return setTriggersAdded((prev) => prev.concat(newTrigger));
   };

   const renderFlowTriggers = () => {
      const mappedTriggers = newFlowTriggersData.map((trigger) => (
         <SoftBox
            key={trigger.label}
            className="no-dark-box-shadow"
            sx={{
               border: "1px solid #474747",
               borderRadius: ".5rem",
               boxShadow: "4px 4px 8px 4px rgba(0,0,0,.1)",
               padding: ".5rem 0"
            }}
         >
            <NewFlowTrigger trigger={trigger} onClick={() => handleTriggerAddition(trigger)} isUsed={triggersAdded.find((innerTrigger) => innerTrigger.label === trigger.label)} />
         </SoftBox>
      ));

      return mappedTriggers;
   };

   const createIntegrationsOptions = () => {
      const mappedIntegrations = integrations.map((integration) => ({
         value: integration.id,
         label: integration.integration_name
      }));

      return mappedIntegrations;
   };

   const handlePreviousStep = () => {
      if (wizardStep === 1) return;

      setWizardStep((prev) => prev - 1);
   };

   useEffect(() => {
      if (!showNewFlowDialog) {
         setWizardStep(1);
         setTriggersAdded([]);
         setSelectedIntegrations([]);
         setPictureUpload(null);

         return;
      }
   }, [showNewFlowDialog]);

   return (
      <Dialog open={showNewFlowDialog} fullWidth={true} maxWidth="csm">
         <DialogTitle
            id="new-flow-dialog-title"
            sx={{
               marginBottom: "20px",
               padding: "32px 0 0 48px"
            }}
            className="white-to-dark"
         >
            {t("common.new_flow")}
         </DialogTitle>
         <DialogContent
            sx={{
               display: wizardStep === 2 ? "none" : "block",
               padding: "0 48px 24px"
            }}
         >
            <Formik initialValues={initialValues} validationSchema={validations} onSubmit={handleSaveSubmit} innerRef={formRef}>
               {({ errors, touched, setFieldValue }) => {
                  return (
                     <Form id={formId} autoComplete="off">
                        <SoftBox
                           className="border-top"
                           sx={{
                              alignItems: "flex-start",
                              display: "flex",
                              paddingTop: "20px"
                           }}
                        >
                           <EditPictureMenu pictureUpload={pictureUpload} setPictureUpload={setPictureUpload} value="" params="" defaultPicture={defaultFlowIcon} />
                           <SoftBox
                              sx={{
                                 flexGrow: 1,
                                 transform: "translateY(-12px)"
                              }}
                           >
                              <CxfFormField
                                 id="flowName"
                                 type={flowName.type}
                                 label={flowName.label}
                                 name={flowName.name}
                                 onInput={truncateInputValue(50)}
                                 validate={validateFlowName}
                                 error={errors.flowName && touched.flowName}
                                 placeholder={flowName.placeholder ? flowName.placeholder : ""}
                                 required={true}
                              />
                              <CxfFormField
                                 type={description.type}
                                 label={description.label}
                                 name={description.name}
                                 onInput={truncateInputValue(500)}
                                 error={errors[description.name] && touched[description.name]}
                                 placeholder={description.placeholder ? description.placeholder : ""}
                                 isMultiline
                                 rows={2}
                              />
                           </SoftBox>
                        </SoftBox>
                        <SoftBox>
                           <SoftTypography variant="h6" mb={1} sx={{ fontSize: "12px" }}>
                              Integrations
                           </SoftTypography>
                           <CreatableSoftSelect
                              className="responsive-multi-height"
                              placeholder="Select Integrations"
                              options={createIntegrationsOptions()}
                              isMulti={true}
                              value={selectedIntegrations}
                              sx={{ zIndex: 0 }}
                              hideSelectedOptions={false}
                              onChange={(selectedOptions, meta) => {
                                 if (meta.action === "create-option") {
                                    const createdOption = meta.option;

                                    // this means there are already created options with the new value
                                    if (selectedIntegrations.some((option) => option.value.toLowerCase().trim() === createdOption.value.toLowerCase().trim())) {
                                       return;
                                    }
                                 }

                                 const values = selectedOptions.map((option) => option.value);
                                 setFieldValue(integration.name, values);
                                 setSelectedIntegrations(selectedOptions);
                              }}
                           />
                        </SoftBox>
                     </Form>
                  );
               }}
            </Formik>
         </DialogContent>
         <DialogContent
            sx={{
               display: wizardStep === 1 ? "none" : "block",
               padding: "0 48px"
            }}
         >
            <SoftBox className="border-top" sx={{ paddingTop: "20px" }}>
               <SoftTypography
                  className="white-to-dark"
                  color="text"
                  sx={{
                     fontSize: {
                        sm: "16px",
                        lg: "20px"
                     },
                     marginBottom: "1rem",
                     lineHeight: "1.25",
                     fontWeight: "600"
                  }}
               >
                  How would you like to trigger your flow? You can make an optional trigger selection now or at any time later.
               </SoftTypography>
               <SoftTypography
                  color="text"
                  sx={{
                     fontSize: "14px",
                     marginBottom: "1.5rem",
                     lineHeight: "1.25",
                     fontWeight: "400"
                  }}
               >
                  Through an internal trigger
               </SoftTypography>
               <SoftBox
                  sx={{
                     display: "grid",
                     gap: "1rem",
                     gridTemplateColumns: "repeat(2, 200px)"
                  }}
               >
                  {renderFlowTriggers()}
               </SoftBox>
            </SoftBox>
         </DialogContent>
         <DialogActions>
            <SoftTypography
               color="text"
               sx={{
                  fontSize: "14px",
                  fontWeight: 600,
                  marginRight: "auto"
               }}
            >
               {wizardStep} of 2
            </SoftTypography>
            {wizardStep !== 1 && (
               <SoftButton
                  color="primary"
                  onClick={handlePreviousStep}
                  sx={{
                     position: "absolute",
                     right: "224px"
                  }}
               >
                  Back
               </SoftButton>
            )}
            <SoftButton variant="outlined" color="info" onClick={cancelBtnHandler}>
               {t("buttons.cancel")}
            </SoftButton>
            {wizardStep === 1 && (
               <SoftButton disabled={submitting} color="primary" onClick={handleSubmit}>
                  Next
               </SoftButton>
            )}
            {wizardStep === 2 && (
               <SoftButton disabled={submitting} color="primary" onClick={handleSubmit}>
                  {t("buttons.create")}
               </SoftButton>
            )}
         </DialogActions>
      </Dialog>
   );
}

export default NewFlowDialog;
